import { push } from 'react-router-redux';
import type { RemoteData, RemoteState } from '../async-actions';
import { createFetchAction, isRemoteSuccess } from '../async-actions';
import type {
	Category,
	CategoryRestrictionRule,
	GetCategoryRulesPageDataResult,
	GetSubCategoriesResult,
	Rule,
} from '../ogm-hub';
import * as Hub from '../ogm-hub';
import { GetSubCategories } from '../remote/hubs/Web_Controllers_V3_RestrictionsController';

import { clone } from '../helpers/CommonUtils';
import { getSession } from '../helpers/Session';
import { getSiteTypeFromId } from './shared';

// ======
// State
// ======

export interface CategoryRulePageState {
	page: RemoteData<CategoryRulePageLoadedState, string[]>;
	listId: number;
	siteType: 'dssi' | 'sysco' | null;
	isSaving: boolean;
	isDirty: boolean;
}

export interface CategoryRulePageLoadedState {
	title: string;
	categoryData: Map<number, CategoryWithParent>;
	rulesData: Map<number, RuleWithParent>;
	categoryBlocks: CategoryBlock[];
	rulesSummary: RulesSummary[];
}

// The WithParent interfaces are the backing data store
// of the page, but are not in the tree shape that the UI
// wants. They can be modified easily, and then the buildTree methods
// will pick them apart to output more UI friendly shapes.

export type CategoryWithParent = Category & {
	parentId: number | null;
	isOpen: boolean;
	remoteState: RemoteState;
};

export type RuleWithParent = Rule & {
	parentId: number | null;
};

// The CategoryBlock and RulesSummary interfaces represent
// the transformed output that is displayed on the page.
// Their data should not be relied upon as a data source.

export interface CategoryBlock {
	id: number;
	key: string;
	title: string;
	restrictionState: RestrictionStateString;
	restrictionMessage: string;
	isOpen: boolean;
	children: RemoteData<CategoryBlock[], string[]>;
	isParent: boolean;
}

export interface RulesSummary {
	key: string;
	title: string;
	status: RestrictionStateString;
}

type RestrictionStateString = 'inherit' | 'restricted' | 'unrestricted';

// ========
// Reducer
// ========

export function initialState(): CategoryRulePageState {
	return {
		page: { type: 'initial' },
		listId: -1,
		isSaving: false,
		isDirty: false,
		siteType: null,
	};
}

export function CategoryRulePageReducer(
	state: CategoryRulePageState = initialState(),
	action: CategoryRuleAction
): CategoryRulePageState {
	switch (action.type) {
		case '@cat-restr/CATEGORY_RULE_PAGE_LOADED_FAILURE': {
			return {
				...state,
				page: { type: 'failure', err: action.errors },
			};
		}
		case '@cat-restr/CATEGORY_RULE_PAGE_LOADED_PENDING': {
			return {
				...initialState(),
				listId: action.payload.listId,
				siteType: getSiteTypeFromId(action.payload.siteTypeId),
			};
		}
		case '@cat-restr/CATEGORY_RULE_PAGE_LOADED_SUCCESS': {
			const { categories, supplierName, listName, rules } = action.payload;

			const categoryData = new Map<number, CategoryWithParent>(
				categories
					.map<CategoryWithParent>((x) => ({
						...x,
						parentId: null,
						remoteState: 'success',
						isOpen: false,
					}))
					.map<[number, CategoryWithParent]>((x) => [x.id, x])
			);

			const rulesData = new Map<number, RuleWithParent>(
				rules.map<[number, RuleWithParent]>((x) => [
					x.categoryId,
					{ ...x, parentId: null },
				])
			);

			const categoryBlocks = buildCategoryTree(categoryData, rulesData);
			const rulesSummary = buildRulesTree(rulesData, categoryData);
			const title =
				state.siteType === 'dssi' ? `${supplierName} - ${listName}` : listName;

			const pageData = {
				title,
				categoryData,
				categoryBlocks,
				rulesData,
				rulesSummary,
			};

			return {
				...state,
				page: { type: 'success', data: pageData },
			};
		}
		case '@cat-restr/CATEGORY_EXPANDED_PENDING': {
			const page = state.page;

			if (!isRemoteSuccess(page)) {
				return state;
			}

			const categoryId = action.payload;

			const nextDataMap = new Map(page.data.categoryData);
			const nextData = clone(nextDataMap.get(categoryId));
			nextData.remoteState = 'loading';
			nextDataMap.set(categoryId, nextData);

			const blocks = buildCategoryTree(nextDataMap, page.data.rulesData);

			const nextPage = {
				...page,
				data: {
					...page.data,
					categoryData: nextDataMap,
					categoryBlocks: blocks,
				},
			};

			return {
				...state,
				page: nextPage,
			};
		}
		case '@cat-restr/CATEGORY_EXPANDED_SUCCESS': {
			const page = state.page;

			if (!isRemoteSuccess(page)) {
				return state;
			}

			const { categoryId, remote } = action.payload;
			const { categories } = remote;

			const nextDataMap = new Map(page.data.categoryData);

			// mark parent as success
			const nextData = clone(nextDataMap.get(categoryId));
			nextData.remoteState = 'success';
			nextData.isOpen = true;
			nextDataMap.set(categoryId, nextData);

			// create a data entry for each one and set them to initial state
			for (const cat of categories) {
				const newCat: CategoryWithParent = {
					...cat,
					parentId: categoryId,
					remoteState: 'initial',
					isOpen: false,
				};
				nextDataMap.set(newCat.id, newCat);
			}

			const blocks = buildCategoryTree(nextDataMap, page.data.rulesData);

			const nextPage = {
				...page,
				data: {
					...page.data,
					categoryData: nextDataMap,
					categoryBlocks: blocks,
				},
			};

			return {
				...state,
				page: nextPage,
			};
		}
		case '@cat-restr/RULES_SAVED_PENDING': {
			return {
				...state,
				isSaving: true,
			};
		}
		case '@cat-restr/RULES_SAVED_FAILURE': {
			return {
				...state,
				page: { type: 'failure', err: action.errors },
				isSaving: false,
			};
		}
		case '@cat-restr/RULES_SAVED_SUCCESS': {
			return {
				...state,
				isSaving: false,
				isDirty: false,
			};
		}
		case '@cat-restr/RULE_CHANGED': {
			const page = state.page;

			if (!isRemoteSuccess(page)) {
				return state;
			}

			const { categoryId, change } = action.payload;

			const nextRulesMap = new Map(page.data.rulesData);

			if (change.field === 'setting') {
				const setting = change.value;
				if (setting === 'inherit') {
					const existingRule = nextRulesMap.get(categoryId);
					const nextRule: RuleWithParent = {
						...existingRule,
						restrictionState: stringToRestrictionState(setting),
					};

					nextRulesMap.set(categoryId, nextRule);
				} else {
					const data = page.data.categoryData.get(categoryId);
					const existingRule = nextRulesMap.get(categoryId);
					let restrictionMessage = '';

					if (existingRule) {
						restrictionMessage = existingRule.restrictionMessage;
					}

					const { title, parentId } = data;
					nextRulesMap.set(categoryId, {
						categoryId,
						title,
						restrictionState: stringToRestrictionState(setting),
						parentId,
						restrictionMessage,
						parentPath: [],
					});
				}
			}

			if (change.field === 'message') {
				const restrictionMessage = change.value;
				const existingRule = nextRulesMap.get(categoryId);
				nextRulesMap.set(categoryId, {
					...existingRule,
					restrictionMessage,
				});
			}

			const blocks = buildCategoryTree(page.data.categoryData, nextRulesMap);
			const rules = buildRulesTree(nextRulesMap, page.data.categoryData);

			const nextPage = {
				...page,
				data: {
					...page.data,
					categoryBlocks: blocks,
					rulesData: nextRulesMap,
					rulesSummary: rules,
				},
			};

			return {
				...state,
				page: nextPage,
				isDirty: true,
			};
		}
		case '@cat-restr/CATEGORY_COLLAPSED': {
			const page = state.page;

			if (!isRemoteSuccess(page)) {
				return state;
			}

			const categoryId = action.payload;

			const nextDataMap = new Map(page.data.categoryData);
			const nextData = clone(nextDataMap.get(categoryId));
			nextData.isOpen = false;
			nextDataMap.set(categoryId, nextData);

			const blocks = buildCategoryTree(nextDataMap, page.data.rulesData);

			const nextPage = {
				...page,
				data: {
					...page.data,
					categoryData: nextDataMap,
					categoryBlocks: blocks,
				},
			};

			return {
				...state,
				page: nextPage,
			};
		}

		case '@cat-restr/CATEGORY_RULE_PAGE_UNLOADED': {
			return {
				...state,
				isDirty: false,
			};
		}

		default: {
			return state;
		}
	}
}

function stringToRestrictionState(str: RestrictionStateString): number {
	switch (str) {
		case 'restricted':
			return Hub.RestrictionState.Restricted;
		case 'unrestricted':
			return Hub.RestrictionState.Unrestricted;
		case 'inherit':
			return Hub.RestrictionState.Inherit;
	}
}

function restrictionStateToString(state: number): RestrictionStateString {
	switch (state) {
		case Hub.RestrictionState.Restricted:
			return 'restricted';
		case Hub.RestrictionState.Unrestricted:
			return 'unrestricted';
		case Hub.RestrictionState.Inherit:
			return 'inherit';
	}
}

function buildCategoryTree(
	categoryData: Map<number, CategoryWithParent>,
	ruleData: Map<number, RuleWithParent>
): CategoryBlock[] {
	const roots = Array.from(categoryData.values())
		.filter((x) => x.parentId === null)
		.map<CategoryBlock>((x) => transformCategory(x, categoryData, ruleData))
		.sort((a, b) => a.key.localeCompare(b.key));

	return roots;
}

function transformCategory(
	payload: CategoryWithParent,
	categoryData: Map<number, CategoryWithParent>,
	ruleData: Map<number, RuleWithParent>
): CategoryBlock {
	let children: RemoteData<CategoryBlock[], string[]> = { type: 'initial' };

	if (payload.remoteState === 'loading') {
		children = { type: 'loading' };
	}

	if (payload.remoteState === 'success') {
		const childrenData = Array.from(categoryData.values())
			.filter((x) => x.parentId === payload.id)
			.map((x) => transformCategory(x, categoryData, ruleData))
			.sort((a, b) => a.key.localeCompare(b.key));
		children = { type: 'success', data: childrenData };
	}

	const matchingRule = ruleData.get(payload.id);

	const restrictionState = matchingRule
		? matchingRule.restrictionState
		: Hub.RestrictionState.Inherit;

	const restrictionMessage = matchingRule
		? matchingRule.restrictionMessage
		: '';

	return {
		id: payload.id,
		key: payload.title,
		title: payload.title,
		isOpen: payload.isOpen,
		restrictionState: restrictionStateToString(restrictionState),
		restrictionMessage,
		children,
		isParent: payload.isParent,
	};
}

function getTitleFromCategory(
	payload: CategoryWithParent,
	categoryData: Map<number, CategoryWithParent>
): string {
	const myTitle = payload.title;
	const parentId = payload.parentId;

	// base case: no parent
	if (!parentId) {
		return payload.title;
	}

	const parent = categoryData.get(parentId);
	const parentTitle = getTitleFromCategory(parent, categoryData);
	return `${parentTitle} / ${myTitle}`;
}

function getRuleTitle(
	payload: RuleWithParent,
	categoryData: Map<number, CategoryWithParent>
): string {
	const myTitle = payload.title;
	// base case: server told us what the path is
	if (payload.parentPath.length > 0) {
		const serverTitle = payload.parentPath.map((x) => x.title).join(' / ');
		return `${serverTitle} / ${myTitle}`;
	}

	// base case: no parent
	if (!payload.parentId) {
		return myTitle;
	}

	const parent = categoryData.get(payload.parentId);
	const parentTitle = getTitleFromCategory(parent, categoryData);

	return `${parentTitle} / ${myTitle}`;
}

function transformRule(
	payload: RuleWithParent,
	categoryData: Map<number, CategoryWithParent>
): RulesSummary {
	return {
		key: getRuleTitle(payload, categoryData),
		title: getRuleTitle(payload, categoryData),
		status: restrictionStateToString(payload.restrictionState),
	};
}

function buildRulesTree(
	rulesData: Map<number, RuleWithParent>,
	categoryData: Map<number, CategoryWithParent>
): RulesSummary[] {
	return Array.from(rulesData.values())
		.filter((x) => x.restrictionState !== Hub.RestrictionState.Inherit)
		.map((x) => transformRule(x, categoryData))
		.sort((a, b) => a.key.localeCompare(b.key));
}

// ========
// Actions
// ========

export type CategoryRuleAction =
	| CATEGORY_RULE_PAGE_LOADED_PENDING
	| CATEGORY_RULE_PAGE_LOADED_FAILURE
	| CATEGORY_RULE_PAGE_LOADED_SUCCESS
	| CATEGORY_RULE_PAGE_UNLOADED
	| CATEGORY_EXPANDED_PENDING
	| CATEGORY_EXPANDED_FAILURE
	| CATEGORY_EXPANDED_SUCCESS
	| CATEGORY_COLLAPSED
	| RULE_CHANGED
	| RULES_SAVED_PENDING
	| RULES_SAVED_FAILURE
	| RULES_SAVED_SUCCESS;

export interface CATEGORY_RULE_PAGE_LOADED_PENDING {
	type: '@cat-restr/CATEGORY_RULE_PAGE_LOADED_PENDING';
	payload: {
		listId: number;
		siteTypeId: number;
	};
}

export interface CATEGORY_RULE_PAGE_LOADED_FAILURE {
	type: '@cat-restr/CATEGORY_RULE_PAGE_LOADED_FAILURE';
	errors: string[];
}

export interface CATEGORY_RULE_PAGE_LOADED_SUCCESS {
	type: '@cat-restr/CATEGORY_RULE_PAGE_LOADED_SUCCESS';
	payload: GetCategoryRulesPageDataResult;
}

export const categoryRulePageLoaded = createFetchAction<
	number,
	CATEGORY_RULE_PAGE_LOADED_PENDING,
	CATEGORY_RULE_PAGE_LOADED_FAILURE,
	CATEGORY_RULE_PAGE_LOADED_SUCCESS
>(
	'@cat-restr/CATEGORY_RULE_PAGE_LOADED_PENDING',
	'@cat-restr/CATEGORY_RULE_PAGE_LOADED_FAILURE',
	'@cat-restr/CATEGORY_RULE_PAGE_LOADED_SUCCESS',
	(listId) => Hub.RestrictionsController.GetCategoryRulesPageData(listId),
	(listId) => {
		const { siteTypeId } = getSession();
		return {
			listId,
			siteTypeId,
		};
	}
);

export interface CATEGORY_EXPANDED_PENDING {
	type: '@cat-restr/CATEGORY_EXPANDED_PENDING';
	payload: number;
}
export interface CATEGORY_EXPANDED_FAILURE {
	type: '@cat-restr/CATEGORY_EXPANDED_FAILURE';
	errors: string[];
}
export interface CATEGORY_EXPANDED_SUCCESS {
	type: '@cat-restr/CATEGORY_EXPANDED_SUCCESS';
	payload: {
		categoryId: number;
		remote: GetSubCategoriesResult;
	};
}

export const categoryExpanded = createFetchAction<
	number,
	CATEGORY_EXPANDED_PENDING,
	CATEGORY_EXPANDED_FAILURE,
	CATEGORY_EXPANDED_SUCCESS
>(
	'@cat-restr/CATEGORY_EXPANDED_PENDING',
	'@cat-restr/CATEGORY_EXPANDED_FAILURE',
	'@cat-restr/CATEGORY_EXPANDED_SUCCESS',
	(args) => {
		const categoryId = args;
		return GetSubCategories(categoryId).then((res) => {
			if (res.ok) {
				return {
					categoryId,
					remote: res.value,
				};
			}
		});
	},
	(args) => args
);

export interface CATEGORY_COLLAPSED {
	type: '@cat-restr/CATEGORY_COLLAPSED';
	payload: number;
}

export function categoryCollpased(categoryId: number): CATEGORY_COLLAPSED {
	return {
		type: '@cat-restr/CATEGORY_COLLAPSED',
		payload: categoryId,
	};
}

export interface RULE_CHANGED {
	type: '@cat-restr/RULE_CHANGED';
	payload: {
		categoryId: number;
		change: RuleChangedField;
	};
}

export type RuleChangedField =
	| {
			field: 'setting';
			value: 'inherit' | 'restricted' | 'unrestricted';
	  }
	| {
			field: 'message';
			value: string;
	  };

export function ruleChanged(
	categoryId: number,
	change: RuleChangedField
): RULE_CHANGED {
	return {
		type: '@cat-restr/RULE_CHANGED',
		payload: {
			categoryId,
			change,
		},
	};
}

export interface RULES_SAVED_PENDING {
	type: '@cat-restr/RULES_SAVED_PENDING';
}
export interface RULES_SAVED_FAILURE {
	type: '@cat-restr/RULES_SAVED_FAILURE';
	errors: string[];
}
export interface RULES_SAVED_SUCCESS {
	type: '@cat-restr/RULES_SAVED_SUCCESS';
	payload: {};
}

export const rulesSaved = createFetchAction<
	{},
	RULES_SAVED_PENDING,
	RULES_SAVED_FAILURE,
	RULES_SAVED_SUCCESS
>(
	'@cat-restr/RULES_SAVED_PENDING',
	'@cat-restr/RULES_SAVED_FAILURE',
	'@cat-restr/RULES_SAVED_SUCCESS',
	(args, dispatch, getState) => {
		const { categoryRules } = getState();
		if (categoryRules.page.type !== 'success') {
			throw new Error('cant save before the page is loaded');
		}

		const restrictionListId = categoryRules.listId;
		const rawRules = categoryRules.page.data.rulesData;

		const rules = Array.from(rawRules.values()).map<CategoryRestrictionRule>(
			(x) => ({
				categoryId: x.categoryId,
				restrictionState: x.restrictionState,
				restrictionMessage: x.restrictionMessage,
			})
		);

		return Hub.RestrictionsController.SaveCategoryRules({
			restrictionListId,
			rules,
		}).then((res) => {
			if (res.ok) {
				dispatch(push('/restrictions'));
			}

			return res;
		});
	}
);

export interface CATEGORY_RULE_PAGE_UNLOADED {
	type: '@cat-restr/CATEGORY_RULE_PAGE_UNLOADED';
}

export function categoryRulePageUnloaded(): CATEGORY_RULE_PAGE_UNLOADED {
	return { type: '@cat-restr/CATEGORY_RULE_PAGE_UNLOADED' };
}
